import { useCallback, useState, type MutableRefObject } from 'react';
import type { QRAnswerItem } from '@scripsteam/scrips-signal-ds';
import type { SoapPayload } from './ambient-types';
import type { NarrativesHost, NarrativeAcceptResult } from './use-narrative-offers';
import {
  findNarrativeCollisions,
  narrativeCollisionKey,
  narrativeHasContent,
  toNarrativeOffers,
  type NarrativeCollision,
  type NarrativeOffer,
  type TemplateLike,
} from './to-narrative-answers';

interface ResponseLike {
  answers: QRAnswerItem[];
}

export interface UseAmbientNarrativeHostOptions<R extends ResponseLike> {
  /** The encounter is signed (PROD-2547 / B5): no offer is kept and nothing is written. */
  locked: boolean;
  template: TemplateLike | undefined;
  /** The live note. Read at the click, never from a render. */
  response: R;
  responseRef: MutableRefObject<R>;
  /** The saved note has loaded and can be saved. */
  writable: boolean;
  /** Put dictated wording that lost to the physician's own text on the collision path (PROD-2445). */
  noteCollisions: (collisions: readonly NarrativeCollision[]) => void;
  /** The note's own change path (the same one a click on the form takes). */
  applyResponse: (next: R) => void;
  markDirty: () => void;
  /** Resolves true once a note save that carries the answer reached the server. */
  whenStored: (carries: (response: R) => boolean) => Promise<boolean>;
}

/**
 * PROD-3195: the host half of the dictated narrative offers.
 *
 * A dictated narrative (the HPI, and any other narrative slot) is an OFFER. `onNarratives` keeps
 * what `toNarrativeOffers` returns and writes NOTHING to the note. `host.onAccept` is the only write,
 * and it runs only on the physician's Accept click: it fills the slot, marked `aiAuthored`, only if
 * the slot is still empty. If the physician typed there meanwhile, nothing is written and the dictated
 * wording goes to the collision path ("Use dictated" / "Keep mine").
 *
 * Offers are held in state only. They are not saved: a reload clears the ones nobody decided.
 */
export function useAmbientNarrativeHost<R extends ResponseLike>({
  locked,
  template,
  response,
  responseRef,
  writable,
  noteCollisions,
  applyResponse,
  markDirty,
  whenStored,
}: UseAmbientNarrativeHostOptions<R>) {
  const [offers, setOffers] = useState<readonly NarrativeOffer[]>([]);

  /** Every new Soap frame. Offers only: no write, no dirty mark, so no autosave. */
  const onNarratives = (soap: SoapPayload) => {
    // PROD-2547 (B5): the host refuses too, not only the panel.
    if (locked || !template) return;
    // PROD-2445: the dictated values that LOSE to an answered slot are surfaced, never dropped.
    noteCollisions(findNarrativeCollisions(soap, template, response.answers));
    // The latest analysis replaces the earlier offers.
    setOffers(toNarrativeOffers(soap, template, response.answers));
  };

  const onAccept = useCallback(
    async (offer: NarrativeOffer): Promise<NarrativeAcceptResult> => {
      if (locked || !writable) return false;
      const prev = responseRef.current;
      const current = prev.answers.find((a) => a.linkId === offer.linkId);
      if (current && narrativeHasContent(current.value)) {
        const existing = typeof current.value === 'string' ? current.value.trim() : String(current.value);
        if (existing === offer.text) {
          // OUR OWN WRITE, not the saved note's yet. An earlier Accept put this text in the slot and its
          // save did not confirm. The retry is not a collision: ask for a save that carries it and
          // report that, so the decision is recorded. (Marked dirty so a save does run.)
          const stored = whenStored((r) => r.answers.some((x) => x.linkId === offer.linkId && x.value === offer.text));
          markDirty();
          return stored;
        }
        noteCollisions([
          {
            key: narrativeCollisionKey(offer.linkId, offer.text),
            slot: offer.slot,
            linkId: offer.linkId,
            existing,
            dictated: offer.text,
          },
        ]);
        return 'occupied';
      }
      const next: R = {
        ...prev,
        answers: [
          ...prev.answers.filter((a) => a.linkId !== offer.linkId),
          { linkId: offer.linkId, value: offer.text, aiAuthored: true },
        ],
      };
      const stored = whenStored((r) => r.answers.some((a) => a.linkId === offer.linkId && a.value === offer.text));
      applyResponse(next);
      markDirty();
      return stored;
    },
    [locked, writable, responseRef, noteCollisions, applyResponse, markDirty, whenStored],
  );

  const host: NarrativesHost = {
    offers,
    canAccept: writable,
    isAnswered: (linkId) => response.answers.some((a) => a.linkId === linkId && narrativeHasContent(a.value)),
    onAccept,
  };

  return { onNarratives, host };
}
